import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ScrollArea } from '@rocco/components/scroll-area';
import type { Conversation, Message } from '../../types';
import { groupMessagesByDate } from '../../utils';
import { ChatBoxMessageBubble } from './ChatBoxMessageBubble';

/**
 * The conversation messages in a chat box.
 * - The chat box text size should remain the same regardless of window size,
 *   unlike the rest of the website, so it should use `px` instead of `rem`.
 * @see {@link ChatBox}
 */
export function ChatBoxMessages({
    className,
    conversation,
    messages,
    isOpen,
    isLoadingMore = false,
    hasMore = false,
    onLoadMore,
}: {
    className?: string;
    conversation?: Conversation;
    messages: Message[];
    isOpen: boolean;
    /**
     * The next page of messages is loading.
     */
    isLoadingMore?: boolean;
    /**
     * There are more messages to load.
     */
    hasMore?: boolean;
    /**
     * Load more messages by fetching the next page. Contains a callback to be
     * called when the next page is loaded, this will scroll the container down
     * to keep the same distance to the bottom of the container.
     */
    onLoadMore?: () => void;
}) {
    const [isUserScrolled, setIsUserScrolled] = useState(false);
    const [loadMoreBottomDistance, setLoadMoreBottomDistance] = useState<
        number | null
    >(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
    const groupedMessages = useMemo(() => {
        return groupMessagesByDate(messages);
    }, [messages]);

    // Scroll to the bottom when the chat box is opened or the messages are
    // updated (except when the user has manually scrolled up).
    useEffect(() => {
        if (!isOpen) return;
        // Scroll to the bottom when previously scrolled to the bottom or when
        // the user sends a new message.
        if (!isUserScrolled || messages[0]?.isOptimistic) {
            containerRef.current?.scrollTo({
                top: containerRef.current.scrollHeight,
                behavior: 'instant',
            });
        }
        // Do not pass `isUserScrolled` on purpose.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, messages]);

    // Trigger load more messages when the user scrolls to the top.
    useEffect(() => {
        const sentinel = loadMoreSentinelRef.current;
        const scrollContainer = containerRef.current;
        if (!sentinel || !scrollContainer || !hasMore || isLoadingMore) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (
                    onLoadMore &&
                    entry?.isIntersecting &&
                    hasMore &&
                    !isLoadingMore
                ) {
                    const distanceToBottom =
                        scrollContainer.scrollHeight -
                        scrollContainer.scrollTop -
                        scrollContainer.clientHeight;
                    onLoadMore();
                    // Scroll down when the next page finishes loading.
                    setLoadMoreBottomDistance(distanceToBottom);
                }
            },
            { root: scrollContainer }
        );
        observer.observe(sentinel);

        return () => {
            observer.disconnect();
        };
    }, [hasMore, isLoadingMore, onLoadMore]);

    // Scroll down to keep bottom distance after next page of messages is loaded.
    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new MutationObserver(() => {
            if (loadMoreBottomDistance !== null && !isLoadingMore) {
                if (isUserScrolled) {
                    containerRef.current?.scrollTo({
                        top:
                            containerRef.current.scrollHeight -
                            containerRef.current.clientHeight -
                            loadMoreBottomDistance,
                        behavior: 'instant',
                    });
                }
                setLoadMoreBottomDistance(null);
            }
        });
        observer.observe(containerRef.current, {
            childList: true,
            subtree: true,
        });

        return () => {
            observer.disconnect();
        };
    }, [loadMoreBottomDistance, isLoadingMore, isUserScrolled]);

    return (
        <ScrollArea
            ref={containerRef}
            className={className}
            scrollBarClassName="w-[8px]"
            thumbClassName="bg-gray-500"
            onCustomScroll={(val) => {
                setIsUserScrolled(val.bottom >= 100);
                if (loadMoreBottomDistance !== null) {
                    setLoadMoreBottomDistance(val.bottom);
                }
            }}
        >
            <div className="relative flex select-text flex-col gap-[8px] p-[12px]">
                {hasMore && (
                    <div
                        ref={loadMoreSentinelRef}
                        className="invisible absolute inset-x-0 top-[400px] h-px"
                        aria-hidden="true"
                    ></div>
                )}
                {groupedMessages.map(({ dateString, a11yString, messages }) => (
                    <React.Fragment key={dateString}>
                        <time
                            className="mx-auto my-[8px] block w-fit min-w-[25%] rounded-full bg-black/40 px-[12px] py-[2px] text-center text-[12px] leading-normal text-white"
                            dateTime={a11yString}
                        >
                            {dateString}
                        </time>
                        {messages.map((message, index, messages) => {
                            const previousSender =
                                index > 0
                                    ? messages[index - 1]?.Participant
                                    : undefined;
                            return (
                                <ChatBoxMessageBubble
                                    key={message.ID}
                                    message={message}
                                    professional={
                                        conversation?.__typename ===
                                        'EnquiryConversation'
                                            ? conversation.InitialEnquiry
                                                  .Professional
                                            : undefined
                                    }
                                    hideSender={
                                        message.Participant.ID ===
                                        previousSender?.ID
                                    }
                                />
                            );
                        })}
                    </React.Fragment>
                ))}
            </div>
        </ScrollArea>
    );
}
